import { Component, effect, input, output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FaDuotoneIconComponent } from '@fortawesome/angular-fontawesome';
import { BaseFormComponent } from '../../core/base-classes/base-form-component';
import { AddressForm } from '../../parts/address/address.form';
import { AddressFormValue } from '../../core/models/ui/address-form-value.model';
import { AddressUtilities } from '../../core/utilities/address.utilities';
import { AddressModel, CreateAddressRequest } from '../../core/models/data/address.model';
import { AddressTypes } from '../../core/enums/data.enums';

@Component({
  selector: 'ms-organization-address-form',
  standalone: true,
  imports: [ReactiveFormsModule, FaDuotoneIconComponent, AddressForm],
  templateUrl: './organization-address.form.html'
})
export class OrganizationAddressForm extends BaseFormComponent {

  public addressType = input.required<AddressTypes>();
  public existingAddress = input<AddressModel | undefined>(undefined);

  public cancelRequested = output<void>();
  public backRequested = output<void>();
  public submitted = output<CreateAddressRequest>();

  @ViewChild(AddressForm) addressFormComponent?: AddressForm;

  public addressForm: FormGroup;

  constructor(private fb: FormBuilder) {
    super();
    this.addressForm = this.fb.group({
      address: [null, [Validators.required]]
    });

    effect(() => {
      const existing = this.existingAddress();
      if (existing) {
        const zipPostal = AddressUtilities.combineZip(existing.primaryZip, existing.secondaryZip);

        const value: AddressFormValue = {
          country: existing.country,
          primaryAddress: existing.primaryAddress ?? '',
          secondaryAddress: existing.secondaryAddress ?? '',
          city: existing.city ?? '',
          stateProvince: existing.stateProvince,
          zipPostal: zipPostal
        };
        this.addressForm.patchValue({ address: value });
      } else {
        this.addressForm.reset({ address: null });
        this.addressFormComponent?.reset();
      }
    });
  }

  public onCancel(): void {
    this.cancelRequested.emit();
  }

  public onBack(): void {
    this.backRequested.emit();
  }

  public onSubmit(): void {
    if (this.addressForm.invalid) {
      this.addressForm.markAllAsTouched();
      this.addressFormComponent?.markAllAsTouched();
      return;
    }

    const addressValue = this.addressForm.value.address as AddressFormValue | null;
    if (!addressValue || !addressValue.stateProvince) return;

    const request: CreateAddressRequest = {
      type: this.addressType(),
      primaryAddress: addressValue.primaryAddress,
      secondaryAddress: addressValue.secondaryAddress?.trim() ? addressValue.secondaryAddress : undefined,
      city: addressValue.city,
      stateProvinceId: addressValue.stateProvince.stateProvinceId,
      primaryZip: AddressUtilities.getPrimaryZip(addressValue.zipPostal, addressValue.country?.alpha2Code) ?? '',
      secondaryZip: AddressUtilities.getSecondaryZip(addressValue.zipPostal, addressValue.country?.alpha2Code) ?? undefined
    };

    this.submitted.emit(request);
  }

  public resetForm(): void {
    this.addressForm.reset({ address: null });
    this.addressFormComponent?.reset();
  }
}
